import React from 'react';
import PropTypes from 'prop-types';
import WelcomeStepCard from './WelcomeStepCard';
import GradientProgressBar from '../../shared/components/GradientProgressBar';

/**
 * WelcomeScreen
 * 
 * Onboarding welcome screen shown on the admin dashboard.
 * Guides users through initial setup steps.
 * 
 * Props:
 *  - steps: Array of step objects from useOnboardingProgress
 *  - completedCount: Number of completed items
 *  - totalCount: Total number of items
 *  - onSkip: Callback when user clicks "Skip setup"
 *  - onStepAction: Callback when user clicks a step's action button
 */
export default function WelcomeScreen({ 
  steps = [],
  completedCount = 0,
  totalCount = 0,
  onSkip, 
  onStepAction,
}) {
  const progressPercent = totalCount > 0 
    ? Math.round((completedCount / totalCount) * 100) 
    : 0;

  return (
    <div className="welcome-screen">
      {/* Header */}
      <div className="text-center mb-4 pt-4">
        <div className="welcome-header-icon mb-3">
          <i className="bi bi-rocket-takeoff-fill" />
        </div>
        <h2 className="fw-bold mb-2">Welcome to Maintella</h2>
        <p className="text-muted">
          Let's get you started with these simple steps to set up your workspace
        </p>
      </div>

      {/* Step Cards */}
      <div className="row justify-content-center welcome-cards-row">
        {steps.map((step) => (
          <WelcomeStepCard
            key={step.stepNumber}
            {...step}
            onAction={() => onStepAction?.(step.stepNumber)}
          />
        ))}
      </div>

      {/* Progress Section - same width as cards row */}
      <div className="row justify-content-center">
        <div className="col-lg-12">
          <div className="welcome-bottom-section mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold text-muted">Setup Progress</span>
              <span className="text-muted small">
                {completedCount} of {totalCount} completed
              </span>
            </div>
            <GradientProgressBar
              value={progressPercent}
              variant="green"
              size="md"
            />
          </div>
        </div>
      </div>

      {/* Help Section - same width as cards row */}
      <div className="row justify-content-center">
        <div className="col-lg-12">
          <div className="welcome-bottom-section mt-4">
            <div className="welcome-help-card">
              <div className="d-flex align-items-start gap-3">
                <span className="welcome-help-icon">
                  <i className="bi bi-lightbulb-fill" />
                </span>
                <div>
                  <h6 className="fw-bold mb-1">Need help getting started?</h6>
                  <p className="text-muted small mb-2">
                    Check out our documentation and video tutorials to learn more about Maintella
                  </p>
                  <div className="d-flex flex-wrap gap-3">
                    <a href="#docs" className="welcome-help-link">
                      <i className="bi bi-file-text me-1" />
                      View Docs
                    </a>
                    <a href="#tutorial" className="welcome-help-link">
                      <i className="bi bi-play-circle me-1" />
                      Watch Tutorial
                    </a>
                    <a href="#support" className="welcome-help-link">
                      <i className="bi bi-headset me-1" />
                      Contact Support
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skip Link */}
      <div className="text-center mt-4 pb-4">
        <button
          type="button"
          className="btn btn-link text-muted text-decoration-none"
          onClick={onSkip}
        >
          Skip setup and go to dashboard
        </button>
      </div>
    </div>
  );
}

WelcomeScreen.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      stepNumber: PropTypes.number.isRequired,
      category: PropTypes.string.isRequired,
      categoryColor: PropTypes.string,
      title: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['completed', 'active', 'locked']),
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          completed: PropTypes.bool,
        })
      ),
    })
  ),
  completedCount: PropTypes.number,
  totalCount: PropTypes.number,
  onSkip: PropTypes.func,
  onStepAction: PropTypes.func,
};
